import React, { useEffect, useState, useRef, createElement } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { XIcon, FileTextIcon, AlertCircleIcon, CheckCircleIcon, ShieldIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import * as Sentry from '@sentry/react';
import { validateEmail, isDisposableEmail } from '../utils/validationUtils';
// ─── Cloudflare Turnstile config ────────────────────────────────────────
// Feature flag: flip to `false` to disable the CAPTCHA gate entirely
// (e.g. emergency rollback) without removing any code. The widget will
// not render and handleSubmit will skip the verifyTurnstile call.
const ENABLE_SIGNUP_CAPTCHA = true;
// Cloudflare Turnstile PUBLIC site key. Safe to commit.
// The SECRET key lives in Firebase Secret Manager (TURNSTILE_SECRET_KEY)
// and is only readable by the verifyTurnstile Cloud Function.
const TURNSTILE_SITE_KEY = '0x4AAAAAADYnh4MLjOOURJjN';
// Cloudflare's Turnstile JS API URL. Loaded once per page via <script>.
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
// ─── Signup-velocity telemetry helper ───────────────────────────────────
// Fires the recordSignupOutcome Cloud Function in a fire-and-forget way.
// TELEMETRY ONLY — this function MUST NEVER throw to the caller and MUST
// NEVER affect the signup outcome. See SIGNUP_VELOCITY_TELEMETRY_DIAGNOSIS.md.
type ClientSignupOutcome = 'signup_succeeded' | 'signup_auth_error' | 'signup_disposable_email';
const fireSignupTelemetry = (outcome: ClientSignupOutcome, errorCode?: string): void => {
  try {
    const callable = httpsCallable<{
      flow: 'teacher';
      outcome: ClientSignupOutcome;
      errorCode?: string;
    }, {
      recorded: boolean;
    }>(functions, 'recordSignupOutcome');
    // Intentionally not awaited. Telemetry must not delay or block signup.
    callable({
      flow: 'teacher',
      outcome,
      errorCode
    }).catch(() => {
      // Swallow — telemetry failures are never surfaced to the user.
    });
    // Sentry breadcrumb (no PII — outcome + flow only). Safe in dev too;
    // Sentry is a no-op when not initialized.
    Sentry.addBreadcrumb({
      category: 'signup',
      level: outcome === 'signup_succeeded' ? 'info' : 'warning',
      message: outcome,
      data: {
        flow: 'teacher',
        errorCode
      }
    });
  } catch {
    // Swallow — telemetry must never break the signup flow.
  }
};
// Tell TypeScript that `window.turnstile` exists at runtime once the
// Cloudflare script has loaded.
declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement | string, params: {
        sitekey: string;
        callback?: (token: string) => void;
        'expired-callback'?: () => void;
        'error-callback'?: () => void;
        theme?: 'light' | 'dark' | 'auto';
        size?: 'normal' | 'compact';
      }) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}
export const TeacherSignUpLayout = () => {
  const navigate = useNavigate();
  const {
    registerTeacher,
    currentUser
  } = useAuth();
  const US_STATES = [{
    abbr: 'AL',
    name: 'Alabama'
  }, {
    abbr: 'AK',
    name: 'Alaska'
  }, {
    abbr: 'AZ',
    name: 'Arizona'
  }, {
    abbr: 'AR',
    name: 'Arkansas'
  }, {
    abbr: 'CA',
    name: 'California'
  }, {
    abbr: 'CO',
    name: 'Colorado'
  }, {
    abbr: 'CT',
    name: 'Connecticut'
  }, {
    abbr: 'DE',
    name: 'Delaware'
  }, {
    abbr: 'FL',
    name: 'Florida'
  }, {
    abbr: 'GA',
    name: 'Georgia'
  }, {
    abbr: 'HI',
    name: 'Hawaii'
  }, {
    abbr: 'ID',
    name: 'Idaho'
  }, {
    abbr: 'IL',
    name: 'Illinois'
  }, {
    abbr: 'IN',
    name: 'Indiana'
  }, {
    abbr: 'IA',
    name: 'Iowa'
  }, {
    abbr: 'KS',
    name: 'Kansas'
  }, {
    abbr: 'KY',
    name: 'Kentucky'
  }, {
    abbr: 'LA',
    name: 'Louisiana'
  }, {
    abbr: 'ME',
    name: 'Maine'
  }, {
    abbr: 'MD',
    name: 'Maryland'
  }, {
    abbr: 'MA',
    name: 'Massachusetts'
  }, {
    abbr: 'MI',
    name: 'Michigan'
  }, {
    abbr: 'MN',
    name: 'Minnesota'
  }, {
    abbr: 'MS',
    name: 'Mississippi'
  }, {
    abbr: 'MO',
    name: 'Missouri'
  }, {
    abbr: 'MT',
    name: 'Montana'
  }, {
    abbr: 'NE',
    name: 'Nebraska'
  }, {
    abbr: 'NV',
    name: 'Nevada'
  }, {
    abbr: 'NH',
    name: 'New Hampshire'
  }, {
    abbr: 'NJ',
    name: 'New Jersey'
  }, {
    abbr: 'NM',
    name: 'New Mexico'
  }, {
    abbr: 'NY',
    name: 'New York'
  }, {
    abbr: 'NC',
    name: 'North Carolina'
  }, {
    abbr: 'ND',
    name: 'North Dakota'
  }, {
    abbr: 'OH',
    name: 'Ohio'
  }, {
    abbr: 'OK',
    name: 'Oklahoma'
  }, {
    abbr: 'OR',
    name: 'Oregon'
  }, {
    abbr: 'PA',
    name: 'Pennsylvania'
  }, {
    abbr: 'RI',
    name: 'Rhode Island'
  }, {
    abbr: 'SC',
    name: 'South Carolina'
  }, {
    abbr: 'SD',
    name: 'South Dakota'
  }, {
    abbr: 'TN',
    name: 'Tennessee'
  }, {
    abbr: 'TX',
    name: 'Texas'
  }, {
    abbr: 'UT',
    name: 'Utah'
  }, {
    abbr: 'VT',
    name: 'Vermont'
  }, {
    abbr: 'VA',
    name: 'Virginia'
  }, {
    abbr: 'WA',
    name: 'Washington'
  }, {
    abbr: 'WV',
    name: 'West Virginia'
  }, {
    abbr: 'WI',
    name: 'Wisconsin'
  }, {
    abbr: 'WY',
    name: 'Wyoming'
  }];
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    city: '',
    state: ''
  });
  const [verifyChecked, setVerifyChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [signupComplete, setSignupComplete] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(3);
  const [showFirebaseRulesHelp, setShowFirebaseRulesHelp] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  // Real-time email error shown inline beneath the email input — mirrors
  // the UX in StudentSignUpFormSection so a disposable/invalid email shows
  // immediately as the user types, not only after clicking Create Account.
  const [emailError, setEmailError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: 'Too Short',
    color: 'bg-gray-300'
  });
  const [registrationAttempted, setRegistrationAttempted] = useState(false);
  // ─── Turnstile state ───────────────────────────────────────────────────
  // Holds the most recent Turnstile token. null when the widget hasn't
  // resolved yet, when it expires, or when verification just failed.
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  // DOM container where Cloudflare mounts the widget iframe.
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
  // Widget id returned by window.turnstile.render — used to reset the
  // widget after a failed verification.
  const turnstileWidgetIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (currentUser) {
      if (currentUser.emailVerified) {
        navigate('/teacher-dashboard');
      } else {
        navigate('/teacher-email-verification');
      }
    }
  }, [currentUser, navigate]);
  useEffect(() => {
    setPasswordStrength(calculatePasswordStrength(formData.password));
  }, [formData.password]);
  useEffect(() => {
    if (formData.confirmPassword && formData.password !== formData.confirmPassword) {
      setPasswordError('Passwords do not match');
    } else {
      setPasswordError('');
    }
  }, [formData.password, formData.confirmPassword]);
  // ─── Load Turnstile script once ────────────────────────────────────────
  // Idempotent: if the script tag already exists (e.g. from a prior mount
  // in dev or SPA navigation), we don't add a duplicate. Cloudflare's
  // `render=explicit` mode means we render the widget manually below.
  useEffect(() => {
    if (!ENABLE_SIGNUP_CAPTCHA) return;
    if (typeof window === 'undefined') return;
    if (window.turnstile) return; // already loaded
    if (document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`)) return;
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
    // No cleanup — the script is safe to leave on the page and avoids a
    // re-download if the user navigates back to this form.
  }, []);
  // ─── Render the Turnstile widget into our container ────────────────────
  // Polls briefly until window.turnstile is defined (the script may load
  // after mount), then calls render() exactly once. Tokens are stored in
  // state via callback; expiry / error clears the token so the user must
  // re-verify before they can submit.
  useEffect(() => {
    if (!ENABLE_SIGNUP_CAPTCHA) return;
    if (typeof window === 'undefined') return;
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    const tryRender = () => {
      if (cancelled) return;
      const container = turnstileContainerRef.current;
      if (!container || !window.turnstile) return false;
      if (turnstileWidgetIdRef.current) return true; // already rendered
      try {
        const widgetId = window.turnstile.render(container, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => {
            if (!cancelled) setTurnstileToken(token);
          },
          'expired-callback': () => {
            if (!cancelled) setTurnstileToken(null);
          },
          'error-callback': () => {
            if (!cancelled) setTurnstileToken(null);
          },
          theme: 'light'
        });
        turnstileWidgetIdRef.current = widgetId;
        return true;
      } catch (err) {
        console.error('[Turnstile] render failed:', err);
        return false;
      }
    };
    if (!tryRender()) {
      intervalId = setInterval(() => {
        if (tryRender() && intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }, 200);
    }
    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      // We deliberately do NOT call window.turnstile.remove() on unmount.
    };
  }, []);
  const calculatePasswordStrength = (password: string) => {
    if (!password || password.length < 1) {
      return {
        score: 0,
        label: 'Too Short',
        color: 'bg-gray-300'
      };
    }
    let score = 0;
    if (password.length >= 10) score += 1;
    if (password.length >= 14) score += 1;
    if (/[a-z]/.test(password)) score += 0.5;
    if (/[A-Z]/.test(password)) score += 0.5;
    if (/[0-9]/.test(password)) score += 1;
    if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(password)) score += 1;
    score = Math.min(4, score);
    const strengthMap = [{
      score: 0,
      label: 'Too Short',
      color: 'bg-gray-300'
    }, {
      score: 1,
      label: 'Weak',
      color: 'bg-red-500'
    }, {
      score: 2,
      label: 'Fair',
      color: 'bg-orange-500'
    }, {
      score: 3,
      label: 'Good',
      color: 'bg-yellow-500'
    }, {
      score: 4,
      label: 'Strong',
      color: 'bg-green-500'
    }];
    return strengthMap[Math.floor(score)];
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      id,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
    // Real-time email validation — matches StudentSignUpFormSection so the
    // disposable/invalid email error appears immediately beneath the field
    // as the user types, instead of only on Create Account submit.
    if (id === 'email') {
      if (!value) {
        setEmailError('');
      } else {
        const result = validateEmail(value);
        setEmailError(result.isValid ? '' : result.error || '');
      }
    }
    setErrorMessage('');
  };
  useEffect(() => {
    if (!signupComplete) return;
    const countdownInterval = setInterval(() => {
      setRedirectCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          console.log('Redirecting to email verification page...');
          navigate('/teacher-email-verification');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdownInterval);
  }, [signupComplete, navigate]);
  const validateForm = () => {
    if (!formData.firstName.trim()) {
      setErrorMessage('Please enter your first name');
      return false;
    }
    // ─── Email validation (format + disposable/throwaway domain blocklist) ─
    // Uses the shared validateEmail() from utils/validationUtils so teacher
    // and student signup enforce the exact same rules. Rejecting here runs
    // BEFORE Turnstile and Firebase Auth, so a blocked email never consumes
    // a CAPTCHA token or creates an Auth user.
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      // Telemetry: when the rejection was specifically a disposable-email
      // domain, record it. This lets us count disposable-email pressure
      // per-IP separately from generic format errors (which we don't
      // bother tracking — they're indistinguishable from typos).
      if (isDisposableEmail(formData.email)) {
        fireSignupTelemetry('signup_disposable_email');
      }
      setErrorMessage(emailValidation.error || 'Please enter a valid email address');
      return false;
    }
    if (formData.password.length < 10) {
      setErrorMessage('Password must be at least 10 characters long');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setErrorMessage('Passwords do not match');
      return false;
    }
    if (!formData.city.trim()) {
      setErrorMessage('Please enter your city');
      return false;
    }
    if (!formData.state) {
      setErrorMessage('Please select your state');
      return false;
    }
    if (!verifyChecked) {
      setErrorMessage('Please verify the accuracy of your information');
      return false;
    }
    return true;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (registrationAttempted || isSubmitting) {
      console.log('[Signup] Registration already in progress or completed');
      return;
    }
    setErrorMessage('');
    setSuccessMessage('');
    setShowFirebaseRulesHelp(false);
    if (!validateForm()) return;
    setIsSubmitting(true);
    setRegistrationAttempted(true);
    try {
      // ─── Cloudflare Turnstile gate ────────────────────────────────────
      // Verify BEFORE we touch Firebase Auth. If verification fails the
      // signup is aborted and the user can re-verify and resubmit.
      if (ENABLE_SIGNUP_CAPTCHA) {
        if (!turnstileToken) {
          setErrorMessage('Please complete the verification challenge before submitting.');
          setIsSubmitting(false);
          setRegistrationAttempted(false);
          return;
        }
        // Read honeypot value directly from the DOM. We do not bind it to
        // React state because (a) it should always be empty and (b) we
        // want zero re-renders if a bot happens to fill it.
        const honeypotEl = document.getElementById('mc-website-hp') as HTMLInputElement | null;
        const honeypot = honeypotEl?.value ?? '';
        const verifyFn = httpsCallable<{
          token: string;
          honeypot: string;
          flow: 'teacher';
        }, {
          verified: boolean;
          errors?: string[];
        }>(functions, 'verifyTurnstile');
        let verifyResult;
        try {
          verifyResult = await verifyFn({
            token: turnstileToken,
            honeypot,
            flow: 'teacher'
          });
        } catch (verifyErr) {
          console.error('[Turnstile] verification call failed:', verifyErr);
          setErrorMessage('Verification could not be completed. Please refresh the challenge and try again.');
          // Reset the widget so the user gets a fresh token to retry with.
          if (turnstileWidgetIdRef.current && window.turnstile?.reset) {
            window.turnstile.reset(turnstileWidgetIdRef.current);
          }
          setTurnstileToken(null);
          setIsSubmitting(false);
          setRegistrationAttempted(false);
          return;
        }
        if (!verifyResult.data?.verified) {
          console.warn('[Turnstile] verification rejected:', verifyResult.data?.errors);
          setErrorMessage('Verification failed. Please complete the challenge again and resubmit.');
          if (turnstileWidgetIdRef.current && window.turnstile?.reset) {
            window.turnstile.reset(turnstileWidgetIdRef.current);
          }
          setTurnstileToken(null);
          setIsSubmitting(false);
          setRegistrationAttempted(false);
          return;
        }
        // Telemetry breadcrumb — Turnstile passed for this flow. The
        // server-side counter has already recorded `turnstile_verified`.
        Sentry.addBreadcrumb({
          category: 'signup',
          level: 'info',
          message: 'turnstile_verified',
          data: {
            flow: 'teacher'
          }
        });
      }
      console.log('[Signup] Starting teacher registration process...');
      const displayName = `${formData.firstName} ${formData.lastName}`.trim();
      const city = formData.city.trim();
      const state = formData.state;
      const location = `${city}, ${state}`;
      const result = await registerTeacher(formData.email.trim(), formData.password, formData.firstName.trim(), formData.lastName.trim(), location, city, state);
      console.log('[Signup] Teacher registration successful!', result);
      // Telemetry: account created. Fire-and-forget — never blocks UX.
      fireSignupTelemetry('signup_succeeded');
      setIsSubmitting(false);
      setSuccessMessage('Your teacher account has been created! A verification email has been sent to your inbox.');
      setSignupComplete(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        city: '',
        state: ''
      });
      setVerifyChecked(false);
      navigate('/teacher-email-verification');
    } catch (error: any) {
      console.error('[Signup] Signup error:', error);
      // Telemetry: signup rejected by Firebase Auth (or another downstream
      // failure). The error.code is a Firebase Auth code like
      // 'auth/email-already-in-use' — safe to record as a dimension.
      fireSignupTelemetry('signup_auth_error', typeof error?.code === 'string' ? error.code : undefined);
      setRegistrationAttempted(false);
      if (error.code === 'auth/email-already-in-use') {
        setErrorMessage('This email is already registered. Please sign in instead.');
      } else if (error.code === 'auth/invalid-email') {
        setErrorMessage('Please enter a valid email address.');
      } else if (error.code === 'auth/weak-password') {
        setErrorMessage('Password is too weak. Please use a stronger password.');
      } else if (error.message && error.message.includes('Firebase permissions error')) {
        setErrorMessage('Missing or insufficient permissions.');
        setShowFirebaseRulesHelp(true);
      } else {
        setErrorMessage(error.message || 'An error occurred. Please try again later.');
      }
      setIsSubmitting(false);
    }
  };
  return <div className="w-full" data-id="element-12302">
      <section className="pt-32 pb-16 bg-navy-900 relative overflow-hidden" data-id="element-12303">
        <div className="absolute inset-0 z-0" data-id="element-12304">
          <div className="absolute inset-0 bg-navy-900/70 z-10" data-id="element-12305"></div>
          <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url('https://uploadthingy.s3.us-west-1.amazonaws.com/s8ktVYbcqPGNW3DKAXWQDj/Piano_teacher_3.jpg')`
        }} data-id="element-12306"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-12307">
          <div className="text-center" data-id="element-12308">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold leading-tight mb-6" data-id="element-12309">
              Start Teaching on{' '}
              <span className="text-deep-pink" data-id="element-12310">MaestroConnect</span>
            </h1>
            <p className="text-xl text-white mb-8 max-w-3xl mx-auto" data-id="element-12311">
              Join a growing network of qualified music teachers and connect
              with students in your area or online.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-navy-900/5" data-id="element-12312">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-12313">
          <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 p-8" data-id="element-12314">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-navy-900 mb-6 text-center" data-id="element-12315">
              Create Your Teacher Profile
            </h2>

            {successMessage && <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-start" data-id="element-12316">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-12317" />
                <div data-id="element-12318">
                  <span data-id="element-12319">{successMessage}</span>
                  {signupComplete && <p className="text-sm mt-1" data-id="element-12320">
                      Redirecting to complete your profile in{' '}
                      {redirectCountdown} seconds...
                    </p>}
                </div>
              </div>}

            {errorMessage && <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start" data-id="element-12321">
                <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-12322" />
                <span data-id="element-12323">{errorMessage}</span>
              </div>}

            {showFirebaseRulesHelp && <div className="mb-6 bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg" data-id="element-12324">
                <h3 className="font-medium mb-2" data-id="element-12325">
                  Firebase Security Rules Issue
                </h3>
                <p className="mb-2" data-id="element-12326">
                  Your Firebase security rules need to be updated to allow
                  document creation. Update your Firestore rules to:
                </p>
                <pre className="bg-gray-100 p-3 rounded text-xs overflow-x-auto mb-2" data-id="element-12327">
                  {`rules_version = '2';
service cloud.firestore {
  match /databases/{database}/documents {
    match /users/{userId} {
      allow read: if request.auth != null && request.auth.uid == userId;
      allow create: if request.auth != null && request.auth.uid == userId;
      allow update: if request.auth != null && request.auth.uid == userId;
    }
    match /teachers/{teacherId} {
      allow read: if request.auth != null && request.auth.uid == teacherId;
      allow create: if request.auth != null && request.auth.uid == teacherId;
      allow update: if request.auth != null && request.auth.uid == teacherId;
      match /calendar/{eventId} {
        allow read, write: if request.auth != null && request.auth.uid == teacherId;
      }
    }
    match /students/{studentId} {
      allow read: if request.auth != null && request.auth.uid == studentId;
      allow create: if request.auth != null && request.auth.uid == studentId;
      allow update: if request.auth != null && request.auth.uid == studentId;
    }
  }
}`}
                </pre>
                <p className="text-sm" data-id="element-12328">
                  Please update these rules in your Firebase Console under
                  Firestore Database &gt; Rules.
                </p>
              </div>}

            {!signupComplete && <form onSubmit={handleSubmit} data-id="element-12329">
                <div className="space-y-6" data-id="element-12330">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-12331">
                    <div data-id="element-12332">
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-12333">
                        First Name <span className="text-red-500" data-id="element-12334">*</span>
                      </label>
                      <input type="text" id="firstName" value={formData.firstName} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 transition-colors text-gray-900" placeholder="Your first name" required disabled={isSubmitting} data-id="element-12335" />
                    </div>
                    <div data-id="element-12336">
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-12337">
                        Last Name
                      </label>
                      <input type="text" id="lastName" value={formData.lastName} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 transition-colors text-gray-900" placeholder="Your last name" disabled={isSubmitting} data-id="element-12338" />
                    </div>
                  </div>

                  <div data-id="element-12339">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-12340">
                      Email Address <span className="text-red-500" data-id="element-12341">*</span>
                    </label>
                    <input type="email" id="email" value={formData.email} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 transition-colors text-gray-900" placeholder="you@example.com" required disabled={isSubmitting} data-id="element-12342" />
                    {emailError && <p className="mt-1 text-sm text-red-500" data-id="element-12343">{emailError}</p>}
                  </div>

                  <div data-id="element-12344">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-12345">
                      Password <span className="text-red-500" data-id="element-12346">*</span>
                    </label>
                    <div className="relative" data-id="element-12347">
                      <input type={passwordVisible ? 'text' : 'password'} id="password" value={formData.password} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 transition-colors text-gray-900" placeholder="Create a secure password (min. 10 characters)" required disabled={isSubmitting} data-id="element-12348" />
                      <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700" onClick={() => setPasswordVisible(!passwordVisible)} disabled={isSubmitting} data-id="element-12349">
                        {passwordVisible ? <EyeOffIcon className="h-5 w-5" data-id="element-12350" /> : <EyeIcon className="h-5 w-5" data-id="element-12351" />}
                      </button>
                    </div>
                    <div className="mt-2" data-id="element-12352">
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-12353">
                        <div className={`h-full ${passwordStrength.color} transition-all duration-300`} style={{
                      width: `${passwordStrength.score / 4 * 100}%`
                    }} data-id="element-12354"></div>
                      </div>
                      <div className="mt-1 flex justify-between items-center" data-id="element-12355">
                        <span className={`text-xs font-medium ${passwordStrength.score <= 1 ? 'text-red-500' : passwordStrength.score === 2 ? 'text-orange-500' : passwordStrength.score === 3 ? 'text-yellow-500' : 'text-green-500'}`} data-id="element-12356">
                          {passwordStrength.label}
                        </span>
                        <span className="text-xs text-gray-500" data-id="element-12357">
                          Use 8+ characters, include a number and symbol
                        </span>
                      </div>
                    </div>
                  </div>

                  <div data-id="element-12358">
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-12359">
                      Confirm Password <span className="text-red-500" data-id="element-12360">*</span>
                    </label>
                    <div className="relative" data-id="element-12361">
                      <input type={confirmPasswordVisible ? 'text' : 'password'} id="confirmPassword" value={formData.confirmPassword} onChange={handleInputChange} className={`w-full px-4 py-3 border rounded-lg outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 transition-colors text-gray-900 ${passwordError ? 'border-red-500' : 'border-gray-300'}`} placeholder="Confirm your password" required disabled={isSubmitting} data-id="element-12362" />
                      <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700" onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)} disabled={isSubmitting} data-id="element-12363">
                        {confirmPasswordVisible ? <EyeOffIcon className="h-5 w-5" data-id="element-12364" /> : <EyeIcon className="h-5 w-5" data-id="element-12365" />}
                      </button>
                    </div>
                    {passwordError && <p className="mt-1 text-sm text-red-500" data-id="element-12366">
                        {passwordError}
                      </p>}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-12367">
                    <div data-id="element-12368">
                      <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-12369">
                        City <span className="text-red-500" data-id="element-12370">*</span>
                      </label>
                      <input type="text" id="city" value={formData.city} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 transition-colors text-gray-900" placeholder="City" required disabled={isSubmitting} data-id="element-12371" />
                    </div>
                    <div data-id="element-12372">
                      <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-12373">
                        State <span className="text-red-500" data-id="element-12374">*</span>
                      </label>
                      <select id="state" value={formData.state} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 transition-colors text-gray-900" required disabled={isSubmitting} data-id="element-12375">
                        <option value="" data-id="element-12376">Select a state</option>
                        {US_STATES.map(s => <option key={s.abbr} value={s.abbr} data-id="element-12377">
                            {s.name}
                          </option>)}
                      </select>
                    </div>
                  </div>

                  <div className="flex items-start" data-id="element-12378">
                    <div className="flex items-center h-5" data-id="element-12379">
                      <input id="verify" name="verify" type="checkbox" checked={verifyChecked} onChange={() => setVerifyChecked(!verifyChecked)} className="h-4 w-4 text-deep-pink focus:ring-deep-pink border-gray-300 rounded" required disabled={isSubmitting} data-id="element-12380" />
                    </div>
                    <div className="ml-3 text-sm" data-id="element-12381">
                      <label htmlFor="verify" className="font-medium text-gray-700" data-id="element-12382">
                        I verify the accuracy of my information and agree to the{' '}
                        <Link to="/terms-of-service" className="text-blue-600 hover:text-blue-700" data-id="element-12383">
                          Terms of Service
                        </Link>
                      </label>
                    </div>
                  </div>

                  {/* ─── Honeypot (visually hidden, real users never fill) ─── */}
                  {ENABLE_SIGNUP_CAPTCHA && <div aria-hidden="true" style={{
                position: 'absolute',
                left: '-10000px',
                top: 'auto',
                width: '1px',
                height: '1px',
                overflow: 'hidden'
              }} data-id="element-12384">
                      <label htmlFor="mc-website-hp" data-id="element-12385">
                        Website (leave blank)
                      </label>
                      <input id="mc-website-hp" name="website" type="text" tabIndex={-1} autoComplete="off" defaultValue="" data-id="element-12386" />
                    </div>}

                  {/* ─── Cloudflare Turnstile widget ─── */}
                  {ENABLE_SIGNUP_CAPTCHA && <div className="flex justify-center" data-id="element-12387">
                      <div ref={turnstileContainerRef} data-id="element-12388" />
                    </div>}

                  <button type="submit" disabled={isSubmitting || !!passwordError || registrationAttempted || ENABLE_SIGNUP_CAPTCHA && !turnstileToken} className={`w-full ${isSubmitting || passwordError || registrationAttempted || ENABLE_SIGNUP_CAPTCHA && !turnstileToken ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white font-medium py-3 px-4 rounded-lg transition-colors flex justify-center items-center`} data-id="element-12389">
                    {isSubmitting ? <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-12390">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-12391"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-12392"></path>
                        </svg>
                        Creating Account...
                      </> : 'Start Free'}
                  </button>

                  <p className="text-sm text-gray-500 text-center mt-4" data-id="element-12393">
                    You won't be charged during your 30-day free trial. You can
                    cancel anytime.
                  </p>

                  <p className="text-sm text-gray-500 text-center mt-4" data-id="element-12394">
                    Already have an account?{' '}
                    <Link to="/teacher-login" className="text-blue-600 hover:text-blue-700" data-id="element-12395">
                      Sign In
                    </Link>
                  </p>
                </div>
              </form>}

            {signupComplete && <div className="text-center py-8" data-id="element-12396">
                <div className="bg-green-100 rounded-full p-4 w-20 h-20 mx-auto mb-6 flex items-center justify-center" data-id="element-12397">
                  <CheckCircleIcon className="h-12 w-12 text-green-500" data-id="element-12398" />
                </div>
                <h3 className="text-xl font-medium text-gray-900 mb-2" data-id="element-12399">
                  Account Created Successfully!
                </h3>
                <p className="text-gray-600 mb-6" data-id="element-12400">
                  A verification email has been sent to your inbox. We're
                  redirecting you in {redirectCountdown} seconds.
                </p>
                <button onClick={() => navigate('/teacher-email-verification')} className="bg-deep-pink hover:bg-burgundy text-white font-medium py-2 px-6 rounded-lg transition-colors" data-id="element-12401">
                  Verify Your Email
                </button>
              </div>}
          </div>
        </div>
      </section>
    </div>;
};